import type { PairMessage } from "@cronika/proto/common/trading_pb";
import { Cross, Search } from "@cronika/assets/icons";
import { useTranslation } from "@cronika/locales";
import { cn, Input, Modal, ModalContent } from "@cronika/ui";
import { type FC, useState } from "react";
import { useSymbolIcon } from "@/shared/lib/hooks/useSymbolIcon";
import type { TAddCategory, TCategory } from "../types";
import { PairList } from "./PairList";

type TAddPairMobilePanelProps = {
  isOpen: boolean;
  onClose: () => void;
  pairs: PairMessage[];
  categories: TCategory[];
  selectedCategory: TAddCategory;
  onCategoryChange: (category: TAddCategory) => void;
  activePair: PairMessage | null;
  selectedPairs: PairMessage[];
  favoritePairs?: PairMessage[];
  onToggleFavorite?: (pair: PairMessage) => void;
  selectPairAsActive?: (pairId: string) => void;
  onPairSelect: (pair: PairMessage) => void;
  onRemovePair: (pairId: string) => void;
};

const MobileHeader: FC<{ pair: PairMessage | null; onClose: () => void }> = ({ pair, onClose }) => {
  const icon = useSymbolIcon(pair?.type, pair?.baseAsset, pair?.quoteAsset);

  return (
    <div className="flex items-center justify-between px-4 pt-5">
      <div className="flex items-center gap-2">
        {pair && (
          <>
            {icon ? (
              <img src={icon} alt={pair.symbol} className="h-7 w-7 object-contain" />
            ) : (
              <span className="flex h-7 w-7 items-center justify-center rounded-full  text-[9px] font-medium">
                {pair.baseAsset.slice(0, 2)}
              </span>
            )}
            <span className="text-[15px] font-semibold text-white">{pair.symbol}</span>
          </>
        )}
      </div>
      <button
        type="button"
        onClick={onClose}
        className="flex h-8 w-8 items-center justify-center rounded-lg bg-white/10 text-white/70 transition-colors hover:bg-white/15"
      >
        <Cross className="h-3.5 w-3.5" />
      </button>
    </div>
  );
};

export const AddPairMobilePanel: FC<TAddPairMobilePanelProps> = ({
  isOpen,
  onClose,
  pairs,
  categories,
  selectedCategory,
  onCategoryChange,
  activePair,
  selectedPairs,
  favoritePairs = [],
  onToggleFavorite,
  selectPairAsActive,
  onPairSelect,
  onRemovePair,
}) => {
  const { t } = useTranslation("pairs-control");
  const [searchQuery, setSearchQuery] = useState("");

  return (
    <Modal open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <ModalContent
        hideCloseButton
        backdrop="opaque"
        className="!top-auto !bottom-0 !left-0 !translate-x-0 !translate-y-0 !max-w-full !w-full !rounded-b-none !rounded-t-2xl flex flex-col border-white/10 bg-[#8C8C8C]/15 backdrop-blur-xl !h-[calc(100%-5rem)]"
      >
        {/* Header: active pair + close */}
        <MobileHeader pair={activePair} onClose={onClose} />

        {/* Categories — horizontal icon-only row */}
        <div className="flex items-center gap-1 overflow-x-auto px-2 shrink-0 no-scrollbar">
          {categories.map((cat) => {
            const isActive = selectedCategory === cat.key;
            return (
              <button
                key={cat.key}
                type="button"
                onClick={() => onCategoryChange(cat.key)}
                className={cn(
                  "flex h-10 w-10 shrink-0 items-center justify-center rounded-xl transition-colors",
                  isActive ? "text-[#F4A425]" : "text-[#8C8C8C]",
                )}
              >
                {typeof cat.icon === "string" ? (
                  <span className="text-lg">{cat.icon}</span>
                ) : (
                  <cat.icon className="h-7.5 w-7.5 shrink-0" />
                )}
              </button>
            );
          })}
        </div>

        {/* Search */}
        <div className="px-4 pb-3 shrink-0">
          <Input
            variant="grey-15"
            inputSize="md"
            placeholder={t("pairs-control.search-placeholder")}
            startContent={<Search />}
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
        </div>

        {/* Pair list */}
        <PairList
          pairs={pairs}
          selectedCategory={selectedCategory}
          activePair={activePair}
          selectedPairs={selectedPairs}
          favoritePairs={favoritePairs}
          onToggleFavorite={onToggleFavorite}
          selectPairAsActive={selectPairAsActive}
          onPairSelect={onPairSelect}
          onRemovePair={onRemovePair}
          searchQuery={searchQuery}
        />
      </ModalContent>
    </Modal>
  );
};
